import Link from 'next/link'
import * as React from 'react'

import { Button, buttonVariants } from '@/_components/ui/button'
import { nextLinkHrefFromPathWithOptionalHash } from '@/_lib/next-link-href'
import { cn } from '@/_lib/utils'

import { MButtonNextLink } from './m-button-next-link'

import type { VariantProps } from 'class-variance-authority'

/** Sprite: `public/assets/layout/icons.svg` symbol ids → friendly names for `MButton`. */
const M_BUTTON_LAYOUT_SPRITES = {
	'arrow-1-left': 's_ar1_l',
	'arrow-1-right': 's_ar1_r',
	'arrow-1-up': 's_ar1_u',
	'arrow-1-down': 's_ar1_d',
	'arrow-2-down': 's_ar2_d',
	'arrow-2-up': 's_ar2_u',
	'arrow-2-left': 's_ar2_l',
	'arrow-2-right': 's_ar2_r',
	'arrow-2-right-up': 's_ar2_ru',
	'arrow-2-left-up': 's_ar2_lu',
	'arrow-2-left-down': 's_ar2_ld',
	'arrow-2-right-down': 's_ar2_rd',
	'arrow-3-down': 's_ar3_d',
	'arrow-3-up': 's_ar3_u',
	'arrow-3-right': 's_ar3_r',
	'arrow-3-left': 's_ar3_l',
	'face-smile': 's_face1',
	'face-neutral': 's_face2',
	'face-frown': 's_face3',
	facebook: 's_fb',
	linkedin: 's_in',
	instagram: 's_insta',
	'drag-part-1': 's_drag_p1',
	'drag-part-2': 's_drag_p2',
	search: 's_mag',
	heart: 's_heart',
	menu: 's_menu',
	read: 's_read',
	symbol: 's_symbol',
	'star-part-1': 's_fav_p1',
	'star-part-2': 's_fav_p2',
	close: 's_cross',
	phone: 's_phone'
} as const

export type MButtonIcon = keyof typeof M_BUTTON_LAYOUT_SPRITES

function isArrow2Icon(icon: MButtonIcon): boolean {
	return icon.startsWith('arrow-2-')
}

const M_BUTTON_LAYOUT_SPRITE_HREF = '/assets/layout/icons.svg'

function MButtonLayoutSprite({
	fragmentId,
	className
}: {
	fragmentId: string
	className?: string
}) {
	return (
		<svg className={cn('sico shrink-0', className)} aria-hidden>
			<use href={`${M_BUTTON_LAYOUT_SPRITE_HREF}#${fragmentId}`} />
		</svg>
	)
}

/** Named group for tray colors (`group-hover/mbtn:*`). Swipe motion uses `.mbtn-has-icon` + globals.scss. */
const M_BTN_GROUP = 'group/mbtn'

/** Two stacked sprites; hover motion via `.mbtn-has-icon:hover .mbtn-swipe-*` in `globals.scss`. */
function MButtonIconSwipe({
	fragmentId,
	trayClassName,
	spriteClassName = 'size-3'
}: {
	fragmentId: string
	trayClassName: string
	/** Default `size-3` (12px); `arrow-2-*` icons use 80% for legibility in the tray. */
	spriteClassName?: string
}) {
	return (
		<span
			className={cn(
				'mbtn-icon-tray relative flex shrink-0 flex-row overflow-hidden',
				trayClassName
			)}
		>
			<span
				className={cn(
					'mbtn-swipe-a flex size-full min-w-full flex-none items-center justify-center',
					'transition-[transform] duration-300 ease-out'
				)}
			>
				<MButtonLayoutSprite fragmentId={fragmentId} className={spriteClassName} />
			</span>
			<span
				className={cn(
					'mbtn-swipe-b -ml-[100%] flex size-full min-w-full flex-none items-center justify-center',
					'transition-[transform] duration-300 ease-out'
				)}
			>
				<MButtonLayoutSprite fragmentId={fragmentId} className={spriteClassName} />
			</span>
		</span>
	)
}

type ShadcnButtonVariant = NonNullable<VariantProps<typeof buttonVariants>['variant']>

const medanteBrandVariants = [
	'purple',
	'purpleOutline',
	'fuchsia',
	'fuchsiaOutline',
	'cyclamen',
	'cyclamenOutline',
	'blue',
	'blueOutline',
	'turquoise',
	'turquoiseOutline',
	'sand',
	'sandOutline',
	'white',
	'whiteOutline',
	'order'
] as const

export type MedanteButtonBrandVariant = (typeof medanteBrandVariants)[number]

export type MButtonVariant = ShadcnButtonVariant | MedanteButtonBrandVariant

function isMedanteBrandVariant(variant: unknown): variant is MedanteButtonBrandVariant {
	return (
		typeof variant === 'string' && (medanteBrandVariants as readonly string[]).includes(variant)
	)
}

function linkHrefHasFragment(href: React.ComponentProps<typeof Link>['href']): boolean {
	if (typeof href === 'string') return href.includes('#')
	if (href && typeof href === 'object' && 'hash' in href) {
		const h = (href as { hash?: string }).hash
		return typeof h === 'string' && h.length > 0
	}
	return false
}

type MedanteBrandStyle = {
	base: ShadcnButtonVariant
	className: string
	/** Tailwind color utilities for `MButtonIconSwipe` tray (`group-hover/mbtn:*` = root hover). */
	iconTrayColorClassName?: string
}

/** Solid + outline pairs aligned with `--color-m-*` in `variables.css`. */
const medanteBrandClassName: Record<MedanteButtonBrandVariant, MedanteBrandStyle> = {
	purple: {
		base: 'ghost',
		className:
			'border-transparent bg-m-purple text-white shadow-none hover:bg-m-purple-dark/90 hover:text-white focus-visible:ring-m-purple/35 dark:bg-m-purple dark:text-white dark:hover:bg-m-purple-dark/95',
		iconTrayColorClassName:
			'bg-white text-m-purple group-hover/mbtn:bg-m-purple-1 group-hover/mbtn:text-white dark:bg-white dark:text-m-purple dark:group-hover/mbtn:bg-m-purple-1 dark:group-hover/mbtn:text-white'
	},
	purpleOutline: {
		base: 'outline',
		className:
			'border-m-purple bg-transparent text-m-purple shadow-none hover:border-m-purple-dark hover:bg-m-purple-dark/90 hover:text-white focus-visible:ring-m-purple/35 dark:border-m-purple dark:text-m-purple dark:hover:border-m-purple-dark dark:hover:bg-m-purple-dark/95 dark:hover:text-white',
		iconTrayColorClassName:
			'bg-white text-m-purple group-hover/mbtn:bg-transparent group-hover/mbtn:text-m-purple dark:bg-white dark:text-m-purple dark:group-hover/mbtn:bg-transparent dark:group-hover/mbtn:text-m-purple'
	},
	fuchsia: {
		base: 'ghost',
		className:
			'border-transparent bg-m-fuchsia text-white shadow-none hover:bg-m-fuchsia-dark/95 hover:text-white focus-visible:ring-m-fuchsia/35 dark:bg-m-fuchsia dark:text-white dark:hover:bg-m-fuchsia-dark/95',
		iconTrayColorClassName:
			'bg-white text-m-fuchsia group-hover/mbtn:bg-m-fuchsia-1 group-hover/mbtn:text-white dark:bg-white dark:text-m-fuchsia dark:group-hover/mbtn:bg-m-fuchsia-1 dark:group-hover/mbtn:text-white'
	},
	fuchsiaOutline: {
		base: 'outline',
		className:
			'border-m-fuchsia bg-transparent text-m-fuchsia shadow-none hover:bg-m-fuchsia/10 hover:text-m-fuchsia-dark dark:border-m-fuchsia dark:text-m-fuchsia dark:hover:bg-m-fuchsia/15',
		iconTrayColorClassName:
			'bg-white text-m-fuchsia group-hover/mbtn:bg-transparent group-hover/mbtn:text-m-fuchsia dark:bg-white dark:text-m-fuchsia dark:group-hover/mbtn:bg-transparent dark:group-hover/mbtn:text-m-fuchsia'
	},
	cyclamen: {
		base: 'ghost',
		className:
			'border-transparent bg-m-cyclamen text-white shadow-none hover:bg-m-cyclamen-dark/95 hover:text-white focus-visible:ring-m-cyclamen/35 dark:bg-m-cyclamen dark:text-white dark:hover:bg-m-cyclamen-dark/95',
		iconTrayColorClassName:
			'bg-white text-m-cyclamen group-hover/mbtn:bg-m-cyclamen-1 group-hover/mbtn:text-white dark:bg-white dark:text-m-cyclamen dark:group-hover/mbtn:bg-m-cyclamen-1 dark:group-hover/mbtn:text-white'
	},
	cyclamenOutline: {
		base: 'outline',
		className:
			'border-m-cyclamen bg-transparent text-m-cyclamen shadow-none hover:bg-m-cyclamen/10 hover:text-m-cyclamen-dark dark:border-m-cyclamen dark:text-m-cyclamen dark:hover:bg-m-cyclamen/15',
		iconTrayColorClassName:
			'bg-white text-m-cyclamen group-hover/mbtn:bg-transparent group-hover/mbtn:text-m-cyclamen dark:bg-white dark:text-m-cyclamen dark:group-hover/mbtn:bg-transparent dark:group-hover/mbtn:text-m-cyclamen'
	},
	blue: {
		base: 'ghost',
		className:
			'border-transparent bg-m-blue text-white shadow-none hover:bg-m-blue-dark/90 hover:text-white focus-visible:ring-m-blue/40 dark:bg-m-blue dark:text-white dark:hover:bg-m-blue-dark/90 dark:hover:text-white',
		iconTrayColorClassName:
			'bg-white text-m-blue group-hover/mbtn:bg-m-blue-1 group-hover/mbtn:text-white dark:bg-white dark:text-m-blue dark:group-hover/mbtn:bg-m-blue-1 dark:group-hover/mbtn:text-white'
	},
	blueOutline: {
		base: 'outline',
		className:
			'border-m-blue bg-transparent text-m-blue-dark shadow-none hover:bg-m-blue/15 hover:text-m-purple-dark dark:border-m-blue dark:text-m-blue dark:hover:bg-m-blue/15',
		iconTrayColorClassName:
			'bg-white text-m-blue-dark group-hover/mbtn:bg-transparent group-hover/mbtn:text-m-blue-dark dark:bg-white dark:text-m-blue-dark dark:group-hover/mbtn:bg-transparent dark:group-hover/mbtn:text-m-blue-dark'
	},
	turquoise: {
		base: 'ghost',
		className:
			'border-transparent bg-m-turquoise text-m-purple-dark shadow-none hover:bg-m-turquoise-dark/90 hover:text-m-purple-dark focus-visible:ring-m-turquoise/40 dark:bg-m-turquoise dark:text-m-purple-dark dark:hover:bg-m-turquoise-dark/90',
		iconTrayColorClassName:
			'bg-white text-m-turquoise group-hover/mbtn:bg-m-turquoise-1 group-hover/mbtn:text-m-purple-dark dark:bg-white dark:text-m-turquoise dark:group-hover/mbtn:bg-m-turquoise-1 dark:group-hover/mbtn:text-m-purple-dark'
	},
	turquoiseOutline: {
		base: 'outline',
		className:
			'border-m-turquoise bg-transparent text-m-turquoise-dark shadow-none hover:bg-m-turquoise/15 hover:text-m-purple-dark dark:border-m-turquoise dark:text-m-turquoise dark:hover:bg-m-turquoise/15',
		iconTrayColorClassName:
			'bg-white text-m-turquoise-dark group-hover/mbtn:bg-transparent group-hover/mbtn:text-m-turquoise-dark dark:bg-white dark:text-m-turquoise-dark dark:group-hover/mbtn:bg-transparent dark:group-hover/mbtn:text-m-turquoise-dark'
	},
	sand: {
		base: 'ghost',
		className:
			'border-transparent bg-m-sand-3 text-m-purple-dark shadow-none hover:bg-m-purple hover:text-white focus-visible:ring-m-sand/50 dark:bg-m-sand dark:text-m-purple-dark dark:hover:bg-m-purple dark:hover:text-white',
		iconTrayColorClassName:
			'bg-white text-m-purple-dark group-hover/mbtn:bg-white group-hover/mbtn:text-m-purple-dark dark:bg-white dark:text-m-purple-dark dark:group-hover/mbtn:bg-white dark:group-hover/mbtn:text-m-purple-dark'
	},
	sandOutline: {
		base: 'outline',
		className:
			'border-m-sand bg-transparent text-m-sand-dark shadow-none hover:bg-m-sand/20 hover:text-m-purple-dark dark:border-m-sand dark:text-m-sand dark:hover:bg-m-sand/20',
		iconTrayColorClassName:
			'bg-white text-m-sand-dark group-hover/mbtn:bg-transparent group-hover/mbtn:text-m-sand-dark dark:bg-white dark:text-m-sand dark:group-hover/mbtn:bg-transparent dark:group-hover/mbtn:text-m-sand'
	},
	white: {
		base: 'ghost',
		className:
			'border-transparent bg-white text-m-purple-dark shadow-none hover:bg-white/90 focus-visible:ring-m-purple-dark/35 dark:bg-white dark:text-m-purple-dark dark:hover:bg-white/10',
		iconTrayColorClassName:
			'bg-white text-m-purple-dark group-hover/mbtn:bg-m-purple-1 group-hover/mbtn:text-m-purple-dark dark:bg-white dark:text-m-purple-dark dark:group-hover/mbtn:bg-m-purple-1 dark:group-hover/mbtn:text-m-purple-dark'
	},
	whiteOutline: {
		base: 'outline',
		className:
			'border-white bg-transparent text-white shadow-none hover:bg-white/10 hover:text-m-purple-dark focus-visible:ring-m-purple-dark/35 dark:bg-white dark:text-m-purple-dark dark:hover:bg-white/10',
		iconTrayColorClassName:
			'bg-white text-white group-hover/mbtn:bg-transparent group-hover/mbtn:text-white dark:bg-white dark:text-m-purple-dark dark:group-hover/mbtn:bg-transparent dark:group-hover/mbtn:text-m-purple-dark'
	},
	order: {
		base: 'ghost',
		className:
			'border-transparent bg-m-cyclamen text-white shadow-none hover:bg-m-purple hover:text-white focus-visible:ring-m-purple-dark/35 dark:bg-m-cyclamen dark:text-white dark:hover:bg-m-cyclamen-dark/95',
		iconTrayColorClassName:
			'bg-transparent text-white group-hover/mbtn:bg-m-purple-1 group-hover/mbtn:text-white dark:bg-transparent dark:text-white dark:group-hover/mbtn:bg-m-purple-1 dark:group-hover/mbtn:text-white'
	}
}

export type MButtonProps = Omit<React.ComponentProps<typeof Button>, 'variant'> & {
	variant?: MButtonVariant
	/** When set, renders a Next.js `Link` with the same button styling. */
	to?: React.ComponentProps<typeof Link>['href']
	/** Passed to Next.js `Link` when `to` is set; ignored for plain buttons. */
	prefetch?: React.ComponentProps<typeof Link>['prefetch']
	/** Icon from `/assets/layout/icons.svg`, rendered after the label. */
	icon?: MButtonIcon
}

/**
 * Wraps shadcn `Button` with Medante brand variants (`purple`, `sandOutline`, …)
 * backed by `--color-m-*` tokens in `variables.css`.
 */
function MButton({
	variant = 'default',
	size = 'default',
	className,
	to,
	prefetch,
	icon,
	children,
	onClick,
	...props
}: MButtonProps) {
	const defaultClasses = cn(
		'rounded-full font-normal cursor-pointer duration-400 ease-out',
		icon ? 'pl-5 pr-2' : 'px-5'
	)

	// @ts-expect-error - sizeClasses is a record of size classes
	const sizeClasses: Record<NonNullable<VariantProps<typeof buttonVariants>['size']>, string> = {
		default: 'h-11'
	}
	const sizeClass = sizeClasses[size ?? 'default']

	const shadcnVariant: ShadcnButtonVariant = isMedanteBrandVariant(variant)
		? medanteBrandClassName[variant].base
		: variant

	const trayColors = isMedanteBrandVariant(variant)
		? medanteBrandClassName[variant].iconTrayColorClassName
		: undefined

	const iconSwipeTrayClass = cn(
		'size-[30px] rounded-full transition-colors duration-200 ease-out',
		trayColors,
		!trayColors && 'text-current'
	)

	const mergedClassName = cn(
		isMedanteBrandVariant(variant)
			? cn(defaultClasses, medanteBrandClassName[variant].className, sizeClass)
			: cn(defaultClasses, sizeClass),
		icon && M_BTN_GROUP,
		icon && 'mbtn-has-icon',
		className
	)

	const iconSpriteClass = icon && isArrow2Icon(icon) ? 'size-[0.80rem]' : 'size-[0.80rem]'

	const iconEl = icon ? (
		<MButtonIconSwipe
			fragmentId={M_BUTTON_LAYOUT_SPRITES[icon]}
			trayClassName={iconSwipeTrayClass}
			spriteClassName={iconSpriteClass}
		/>
	) : null

	const label =
		icon != null ? (
			<span className='inline-flex items-center gap-4'>
				{children}
				{iconEl}
			</span>
		) : (
			children
		)

	if (to) {
		const linkHref =
			typeof to === 'string' ? nextLinkHrefFromPathWithOptionalHash(to) : to
		const useHashScroll = linkHrefHasFragment(to)

		return (
			<Button
				variant={shadcnVariant}
				size={size}
				className={mergedClassName}
				asChild
				{...props}
			>
				{useHashScroll ?
					<MButtonNextLink href={linkHref} prefetch={prefetch} hashScroll>
						{label}
					</MButtonNextLink>
				:	<Link href={linkHref} prefetch={prefetch}>
						{label}
					</Link>
				}
			</Button>
		)
	}

	return (
		<Button
			variant={shadcnVariant}
			size={size}
			className={mergedClassName}
			onClick={onClick}
			{...props}
		>
			{label}
		</Button>
	)
}

export { MButton }
